import type {
  ContestDetail,
  ContestListItem,
  EditorialDetail,
  LanguageSummary,
  Paginated,
  ProblemDetail,
  ProblemGenerationJob,
  ProblemListItem,
  PublicProfile,
  RatingHistoryEntry,
  SelfProfile,
  Standings,
  SubmissionDetail,
  SubmissionListItem,
  TagSummary,
} from '@coderush/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiRequest } from './client.js';

/**
 * TanStack Query のキーは配列の先頭をリソース名にそろえる。
 * invalidate をリソース単位でまとめてかけられるようにするため。
 */
export const queryKeys = {
  problems: (params: unknown) => ['problems', params] as const,
  problem: (slug: string) => ['problems', 'detail', slug] as const,
  editorial: (slug: string) => ['problems', 'editorial', slug] as const,
  tags: () => ['tags'] as const,
  languages: () => ['languages'] as const,
  submissions: (params: unknown) => ['submissions', params] as const,
  submission: (id: string) => ['submissions', 'detail', id] as const,
  contests: (params: unknown) => ['contests', params] as const,
  contest: (slug: string) => ['contests', 'detail', slug] as const,
  standings: (slug: string) => ['contests', 'standings', slug] as const,
  me: () => ['me'] as const,
  user: (username: string) => ['users', username] as const,
  userSubmissions: (username: string) => ['users', username, 'submissions'] as const,
  ratingHistory: (username: string) => ['users', username, 'rating-history'] as const,
  ranking: () => ['ranking'] as const,
  adminDashboard: () => ['admin', 'dashboard'] as const,
  adminProblems: () => ['admin', 'problems'] as const,
  adminTestcases: (slug: string) => ['admin', 'testcases', slug] as const,
  adminContests: () => ['admin', 'contests'] as const,
  adminContest: (slug: string) => ['admin', 'contests', 'detail', slug] as const,
  adminGenerationJobs: () => ['admin', 'generation'] as const,
} as const;

// -----------------------------------------------------------------------------
// Problems
// -----------------------------------------------------------------------------

export interface ProblemListParams {
  page: number;
  perPage: number;
  // exactOptionalPropertyTypes を有効にしているため、
  // 「キーはあるが undefined」を明示的に許す必要がある。
  q?: string | undefined;
  difficulty?: string | undefined;
  tag?: string | undefined;
  solved?: string | undefined;
  sort?: string | undefined;
}

export function useProblems(params: ProblemListParams) {
  return useQuery({
    queryKey: queryKeys.problems(params),
    queryFn: () =>
      apiRequest<Paginated<ProblemListItem>>('/api/problems', {
        query: { ...params },
      }),
  });
}

export function useProblem(slug: string): UseQueryResult<ProblemDetail> {
  return useQuery({
    queryKey: queryKeys.problem(slug),
    queryFn: () => apiRequest<ProblemDetail>(`/api/problems/${encodeURIComponent(slug)}`),
    enabled: slug.length > 0,
  });
}

export function useEditorial(slug: string) {
  return useQuery({
    queryKey: queryKeys.editorial(slug),
    queryFn: () => apiRequest<EditorialDetail>(`/api/problems/${encodeURIComponent(slug)}/editorial`),
    enabled: slug.length > 0,
    retry: false,
  });
}

export function useTags() {
  return useQuery({
    queryKey: queryKeys.tags(),
    queryFn: () => apiRequest<TagSummary[]>('/api/problems/meta/tags'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useLanguages() {
  return useQuery({
    queryKey: queryKeys.languages(),
    queryFn: () => apiRequest<LanguageSummary[]>('/api/languages'),
    staleTime: 10 * 60 * 1000,
  });
}

// -----------------------------------------------------------------------------
// Submissions
// -----------------------------------------------------------------------------

export interface SubmissionListParams {
  page: number;
  perPage: number;
  problemSlug?: string | undefined;
  contestSlug?: string | undefined;
  username?: string | undefined;
  status?: string | undefined;
}

export function useSubmissions(params: SubmissionListParams, enabled = true) {
  return useQuery({
    queryKey: queryKeys.submissions(params),
    queryFn: () =>
      apiRequest<Paginated<SubmissionListItem>>('/api/submissions', { query: { ...params } }),
    enabled,
  });
}

/**
 * 提出詳細。判定中は短い間隔でポーリングする (SPEC 59)。
 *
 * Supabase Realtime でも同じことはできるが、
 * 判定は数秒で終わるうえ 1 画面 1 件なので、
 * 接続を張るより素直なポーリングのほうが壊れにくい。
 */
export function useSubmission(id: string) {
  return useQuery({
    queryKey: queryKeys.submission(id),
    queryFn: () => apiRequest<SubmissionDetail>(`/api/submissions/${encodeURIComponent(id)}`),
    enabled: id.length > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'JUDGING' ? 1200 : false;
    },
  });
}

export interface CreateSubmissionVars {
  problemSlug: string;
  languageSlug: string;
  sourceCode: string;
  contestSlug?: string | undefined;
}

export function useCreateSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: CreateSubmissionVars) =>
      apiRequest<{ id: string; status: string; createdAt: string }>('/api/submissions', {
        method: 'POST',
        body: vars,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['submissions'] });
      void queryClient.invalidateQueries({ queryKey: ['problems'] });
    },
  });
}

export interface CustomTestVars {
  problemSlug?: string | undefined;
  languageSlug: string;
  sourceCode: string;
  input: string;
}

export interface CustomTestResult {
  status: string;
  stdout: string;
  stderr: string;
  compileOutput: string | null;
  executionTimeMs: number | null;
  memoryUsageBytes: number | null;
}

export function useCustomTest() {
  return useMutation({
    mutationFn: async (vars: CustomTestVars): Promise<CustomTestResult> => {
      const started = await apiRequest<{ jobId: string }>('/api/submissions/custom-test', {
        method: 'POST',
        body: vars,
      });

      // 結果が出るまでポーリングする。最大 60 秒で打ち切る。
      const deadline = Date.now() + 60_000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        const res = await apiRequest<{ status: string; result: CustomTestResult | null }>(
          `/api/submissions/custom-test/${encodeURIComponent(started.jobId)}`,
        );
        if (res.status === 'COMPLETED' && res.result) {
          return res.result;
        }
        if (res.status === 'FAILED') {
          throw new Error('実行に失敗しました');
        }
      }
      throw new Error('実行がタイムアウトしました');
    },
  });
}

// -----------------------------------------------------------------------------
// Contests
// -----------------------------------------------------------------------------

export function useContests(params: { page: number; perPage: number }) {
  return useQuery({
    queryKey: queryKeys.contests(params),
    queryFn: () => apiRequest<Paginated<ContestListItem>>('/api/contests', { query: { ...params } }),
  });
}

export function useContest(slug: string) {
  return useQuery({
    queryKey: queryKeys.contest(slug),
    queryFn: () => apiRequest<ContestDetail>(`/api/contests/${encodeURIComponent(slug)}`),
    enabled: slug.length > 0,
  });
}

export function useStandings(slug: string, live: boolean) {
  return useQuery({
    queryKey: queryKeys.standings(slug),
    queryFn: () => apiRequest<Standings>(`/api/contests/${encodeURIComponent(slug)}/standings`),
    enabled: slug.length > 0,
    // 開催中だけ自動更新する (SPEC 59)。終了後に無駄な負荷をかけない。
    refetchInterval: live ? 30_000 : false,
  });
}

export function useRegisterContest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      apiRequest<{ registered: boolean }>(`/api/contests/${encodeURIComponent(slug)}/register`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contests'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Users
// -----------------------------------------------------------------------------

export interface UserStats {
  contestCount: number;
}

export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.me(),
    queryFn: () => apiRequest<SelfProfile>('/api/users/me'),
    enabled,
    staleTime: 60_000,
  });
}

export function useUser(username: string) {
  return useQuery({
    queryKey: queryKeys.user(username),
    queryFn: () =>
      apiRequest<PublicProfile & { stats: UserStats }>(
        `/api/users/${encodeURIComponent(username)}`,
      ),
    enabled: username.length > 0,
  });
}

export function useUserSubmissions(username: string) {
  return useQuery({
    queryKey: queryKeys.userSubmissions(username),
    queryFn: () =>
      apiRequest<SubmissionListItem[]>(`/api/users/${encodeURIComponent(username)}/submissions`),
    enabled: username.length > 0,
  });
}

export function useRatingHistory(username: string) {
  return useQuery({
    queryKey: queryKeys.ratingHistory(username),
    queryFn: () =>
      apiRequest<RatingHistoryEntry[]>(
        `/api/users/${encodeURIComponent(username)}/rating-history`,
      ),
    enabled: username.length > 0,
  });
}

export function useRanking() {
  return useQuery({
    queryKey: queryKeys.ranking(),
    queryFn: () => apiRequest<Array<PublicProfile & { rank: number }>>('/api/users'),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiRequest<SelfProfile>('/api/users/me', { method: 'PATCH', body }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.me(), data);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Admin
// -----------------------------------------------------------------------------

export interface AdminDashboard {
  users: number;
  problems: number;
  submissions: number;
  pendingSubmissions: number;
  activeContests: number;
  queues: {
    judge: Record<string, number>;
    rejudge: Record<string, number>;
  };
}

export function useAdminDashboard(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminDashboard(),
    queryFn: () => apiRequest<AdminDashboard>('/api/admin/dashboard'),
    enabled,
    refetchInterval: enabled ? 15_000 : false,
  });
}

export interface AdminProblemRow {
  id: string;
  slug: string;
  title: string;
  status: string;
  difficulty: string;
  submission_count: number;
  accepted_count: number;
  updated_at: string;
}

export function useAdminProblems(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminProblems(),
    queryFn: () => apiRequest<AdminProblemRow[]>('/api/admin/problems'),
    enabled,
  });
}

export interface AdminTestcase {
  id: string;
  name: string;
  input: string;
  expectedOutput: string;
  isSample: boolean;
  groupName: string | null;
  displayOrder: number;
  score: number;
}

export interface AdminTestcases {
  groups: Array<{ name: string; displayOrder: number; score: number }>;
  testcases: AdminTestcase[];
}

export function useAdminTestcases(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminTestcases(slug),
    queryFn: () =>
      apiRequest<AdminTestcases>(`/api/admin/problems/${encodeURIComponent(slug)}/testcases`),
    enabled: enabled && slug.length > 0,
  });
}

/** 命名規則で自動割り当てされたコンテスト。割り当たらなければ返らない。 */
export interface LinkedContest {
  contestSlug: string;
  label: string;
}

export function useSaveProblem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      slug,
      body,
    }: {
      slug: string | null;
      body: Record<string, unknown>;
    }): Promise<{ slug: string; linkedContest?: LinkedContest }> => {
      if (slug === null) {
        const created = await apiRequest<{
          id: string;
          slug: string;
          linkedContest: LinkedContest | null;
        }>('/api/admin/problems', { method: 'POST', body });
        return {
          slug: created.slug,
          ...(created.linkedContest ? { linkedContest: created.linkedContest } : {}),
        };
      }
      await apiRequest<{ updated: boolean }>(`/api/admin/problems/${encodeURIComponent(slug)}`, {
        method: 'PATCH',
        body,
      });
      return { slug };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['problems'] });
    },
  });
}


/** 1 リクエストに詰める JSON のおおよその上限。API の bodyLimit は 1 MiB。 */
const UPLOAD_CHUNK_BYTES = 700 * 1024;

/**
 * testcase をまとめてアップロードする。
 *
 * ファイル 1 つが API の bodyLimit を超えることがあるので、
 * 中身を見て分割して送る。サーバー側は (problem_id, name) の upsert なので、
 * 分割しても順番が入れ替わっても結果は同じになる。
 *
 * 置き換えは最初の 1 回だけ。毎回 replace すると、
 * 2 つ目以降のかたまりが前のかたまりを消してしまう。
 */
export function useUploadTestcases() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      slug,
      testcases,
      replace,
      onProgress,
    }: {
      slug: string;
      testcases: unknown[];
      replace: boolean;
      onProgress?: (done: number, total: number) => void;
    }): Promise<{ count: number; requests: number }> => {
      const chunks: unknown[][] = [];
      let current: unknown[] = [];
      let size = 0;
      for (const testcase of testcases) {
        const bytes = new TextEncoder().encode(JSON.stringify(testcase)).length;
        // 1 件だけで上限を超える場合も、その 1 件は送ってみる。
        // 弾くのはサーバーの役目で、ここで握り潰さない。
        if (current.length > 0 && size + bytes > UPLOAD_CHUNK_BYTES) {
          chunks.push(current);
          current = [];
          size = 0;
        }
        current.push(testcase);
        size += bytes;
      }
      if (current.length > 0) chunks.push(current);

      let count = 0;
      for (const [index, chunk] of chunks.entries()) {
        const result = await apiRequest<{ count: number }>(
          `/api/admin/problems/${encodeURIComponent(slug)}/testcases`,
          { method: 'PUT', body: { testcases: chunk, replace: replace && index === 0 } },
        );
        count += result.count;
        onProgress?.(count, testcases.length);
      }
      return { count, requests: chunks.length };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

/**
 * WJ のまま止まった提出を投げ直す。
 *
 * worker が落ちると job だけが失われ、提出は JUDGING のまま誰にも拾われない。
 * 画面から復旧できるようにしておく。
 */
export function useRequeuePending() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiRequest<{ queued: number }>('/api/admin/requeue-pending', { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['submissions'] });
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useRejudge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiRequest<{ queued: number }>('/api/admin/rejudge', { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['submissions'] });
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Admin: Contests (SPEC 34-40, 87)
// -----------------------------------------------------------------------------

export type ContestAction = 'publish' | 'unpublish' | 'start' | 'end' | 'archive';

export interface AdminContestRow {
  id: string;
  slug: string;
  title: string;
  status: string;
  rule: string;
  rated: boolean;
  startTime: string;
  endTime: string;
  freezeTime: string | null;
  penaltyMinutes: number;
  participantCount: number;
  problemCount: number;
  ratingFinalizedAt: string | null;
  /** サーバーが返す「いま取れる操作」。遷移規則を画面側で持たない。 */
  availableActions: ContestAction[];
}

export interface AdminContestDetail {
  slug: string;
  title: string;
  description: string;
  status: string;
  rule: string;
  rated: boolean;
  startTime: string;
  endTime: string;
  registrationStart: string | null;
  registrationEnd: string | null;
  freezeTime: string | null;
  penaltyMinutes: number;
  ratingFinalizedAt: string | null;
  availableActions: ContestAction[];
  problems: Array<{
    label: string;
    score: number;
    problemSlug: string;
    title: string;
    problemStatus: string;
  }>;
}

export interface FinalizeResult {
  rated: boolean;
  participants: number;
  ratingChanges: number;
  alreadyFinalized: boolean;
}

export function useAdminContests(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminContests(),
    queryFn: () => apiRequest<AdminContestRow[]>('/api/admin/contests'),
    enabled,
    // 開催中の残り時間や参加者数が動くので、開いている間は定期的に更新する。
    refetchInterval: enabled ? 20_000 : false,
  });
}

export function useAdminContest(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminContest(slug),
    queryFn: () =>
      apiRequest<AdminContestDetail>(`/api/admin/contests/${encodeURIComponent(slug)}`),
    enabled: enabled && slug.length > 0,
  });
}

export function useCreateContest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiRequest<{ slug: string }>('/api/admin/contests', { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['contests'] });
    },
  });
}

export function useUpdateContest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: Record<string, unknown> }) =>
      apiRequest<{ updated: boolean }>(`/api/admin/contests/${encodeURIComponent(slug)}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['contests'] });
    },
  });
}

export function useSetContestProblems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: Record<string, unknown> }) =>
      apiRequest<{ count: number }>(
        `/api/admin/contests/${encodeURIComponent(slug)}/problems`,
        { method: 'PUT', body },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['contests'] });
    },
  });
}

export function useContestTransition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, action }: { slug: string; action: ContestAction }) =>
      apiRequest<{
        status: string;
        availableActions: ContestAction[];
        finalize: FinalizeResult | null;
      }>(`/api/admin/contests/${encodeURIComponent(slug)}/status`, {
        method: 'POST',
        body: { action },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['contests'] });
      // レートが動くのでランキングとプロフィールも古くなる。
      void queryClient.invalidateQueries({ queryKey: ['ranking'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useFinalizeContest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      apiRequest<FinalizeResult>(`/api/admin/contests/${encodeURIComponent(slug)}/finalize`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['ranking'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteContest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      apiRequest<{ deleted: boolean }>(`/api/admin/contests/${encodeURIComponent(slug)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['contests'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Admin: 自動作問 (SPEC 54)
// -----------------------------------------------------------------------------

export interface GenerationJobList {
  jobs: ProblemGenerationJob[];
  limits: { minTestcases: number; maxTestcases: number; sampleCount: number };
}

/** 進行中の依頼があるかどうか。polling を止める判断に使う。 */
function hasRunningJob(jobs: ProblemGenerationJob[]): boolean {
  return jobs.some(
    (job) => job.status === 'PENDING' || job.status === 'DRAFTING' || job.status === 'BUILDING',
  );
}

export function useGenerationJobs(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminGenerationJobs(),
    queryFn: () => apiRequest<GenerationJobList>('/api/admin/generation'),
    enabled,
    /*
     * 生成は分単位でかかる。進行中のあいだだけ短い間隔で見に行き、
     * 何も動いていないときは止める。画面を開きっぱなしにされても
     * API を叩き続けない。
     */
    refetchInterval: (query) => {
      if (!enabled) return false;
      const data = query.state.data;
      return data && hasRunningJob(data.jobs) ? 3_000 : false;
    },
  });
}

export function useRequestGeneration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiRequest<ProblemGenerationJob>('/api/admin/generation', { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}
